import type { PatternPaginatedResponse } from '@/entities/pattern';
import type { SavePatternPayload } from '@/entities/pattern-watchlist';

import { baseApi, ENDPOINTS } from '@/shared/api';

import type {
    PaginatedPatternResponse,
    Pattern,
    SearchPatternsParams,
} from '../model/types/pattern';

const patternApi = baseApi
    .enhanceEndpoints({
        addTagTypes: ['Pattern', 'PatternWatchlist', 'PatternSearch'],
    })
    .injectEndpoints({
        endpoints: (build) => ({
            getPatternsByWatchlistId: build.query<Array<Pattern>, number>({
                query: (watchlistId) =>
                    ENDPOINTS.pattern.getByPatternWatchlistId(watchlistId),
                providesTags: ['PatternWatchlist'],
            }),
            toggleFlagField: build.mutation<
                void,
                {
                    id: number;
                    flag_field: boolean;
                }
            >({
                /* eslint-disable @typescript-eslint/naming-convention */
                query: ({ id, flag_field }) => ({
                    url: ENDPOINTS.pattern.updatePattern(id),
                    method: 'PATCH',
                    body: {
                        flag_field,
                    },
                }),
                /* eslint-enable @typescript-eslint/naming-convention */
                invalidatesTags: ['PatternWatchlist'],
            }),
            toggleMonitorField: build.mutation<
                void,
                {
                    id: number;
                    monitor_function: boolean;
                }
            >({
                /* eslint-disable @typescript-eslint/naming-convention */
                query: ({ id, monitor_function }) => ({
                    url: ENDPOINTS.pattern.updatePattern(id),
                    method: 'PATCH',
                    body: {
                        monitor_function,
                    },
                }),
                /* eslint-enable @typescript-eslint/naming-convention */
                invalidatesTags: ['PatternWatchlist'],
            }),
            updatePattern: build.mutation<
                void,
                {
                    id: number;
                    pattern_name: string;
                    color: string;
                }
            >({
                /* eslint-disable @typescript-eslint/naming-convention */
                query: ({ id, pattern_name, color }) => ({
                    url: ENDPOINTS.pattern.updatePattern(id),
                    method: 'PATCH',
                    body: {
                        pattern_name,
                        color,
                    },
                }),
                /* eslint-enable @typescript-eslint/naming-convention */
                invalidatesTags: ['PatternWatchlist'],
            }),
            searchPatterns: build.query<
                PatternPaginatedResponse,
                SearchPatternsParams
            >({
                query: (params) => ({
                    url: ENDPOINTS.pattern.searchPatterns,
                    method: 'GET',
                    params,
                }),
                transformResponse: (response: unknown) => {
                    const typedResponse = response as PaginatedPatternResponse;

                    return {
                        results: typedResponse.results || [],
                        count: typedResponse.count || 0,
                        next: typedResponse.next || null,
                        previous: typedResponse.previous || null,
                    };
                },
                providesTags: ['PatternSearch'],
            }),
            savePattern: build.mutation<void, SavePatternPayload>({
                query: (patterns) => ({
                    url: ENDPOINTS.pattern.savePattern,
                    method: 'POST',
                    body: patterns,
                }),
                invalidatesTags: ['PatternWatchlist'],
            }),
            deletePattern: build.mutation<void, number>({
                query: (id) => ({
                    url: ENDPOINTS.pattern.deletePattern(id),
                    method: 'DELETE',
                }),
                invalidatesTags: ['PatternWatchlist'],
            }),
            getDistinctFilterValues: build.mutation<
                Record<string, string[]>,
                string[]
            >({
                query: (valueList) => ({
                    url: ENDPOINTS.pattern.getDistinctFilterValues,
                    method: 'POST',
                    body: { value_list: valueList },
                }),
            }),
        }),
    });

export const {
    useGetPatternsByWatchlistIdQuery,
    useSearchPatternsQuery,
    useSavePatternMutation,
    useDeletePatternMutation,
    useUpdatePatternMutation,
    useToggleFlagFieldMutation,
    useToggleMonitorFieldMutation,
    useGetDistinctFilterValuesMutation,
} = patternApi;
